// src/components/auth/AuthReduxSync.tsx

import { useEffect, useCallback } from "react";
import { useAuthService, useAuthState } from "@/services/auth";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  setUser,
  setAccessToken,
  clearAccessToken,
  clearAuthState,
} from "@/store/slices/authSlice";

/**
 * This component syncs AuthService state with Redux state.
 * It should be rendered inside AuthProvider but outside of routes.
 *
 * RESPONSIBILITIES:
 * 1. Sync AuthService user state to Redux
 * 2. Fetch and store access token in Redux (single source of truth)
 * 3. Clean up on logout
 *
 * NOTE: We no longer fetch user profile from backend. All user data comes from Auth0.
 * In the future, we may fetch additional metadata from Auth0 app_metadata/user_metadata.
 *
 * TOKEN MANAGEMENT:
 * AuthService handles token refresh internally. This component just fetches
 * tokens and stores them in Redux for synchronous access by other components.
 */
export const AuthReduxSync: React.FC = () => {
  const authService = useAuthService();
  const { user: authUser, isAuthenticated, isLoading } = useAuthState();

  const dispatch = useAppDispatch();
  const { accessToken } = useAppSelector((state) => state.auth);

  // Sync AuthService state to Redux whenever it changes
  useEffect(() => {
    dispatch(
      setUser({
        user: authUser,
        isAuthenticated,
        isLoading,
      })
    );
  }, [authUser, isAuthenticated, isLoading, dispatch]);

  // Fetch and store access token in Redux
  const fetchAndStoreToken = useCallback(async () => {
    try {
      console.log("[AuthReduxSync] Fetching access token from AuthService...");

      // Get token from AuthService (it handles refresh internally)
      const tokenWithExpiry = await authService.getTokenWithExpiry();

      if (!tokenWithExpiry) {
        console.warn("[AuthReduxSync] No token available");
        dispatch(clearAccessToken());
        return null;
      }

      const now = Date.now();
      const expiresIn = Math.max(
        0,
        Math.floor((tokenWithExpiry.expiresAt - now) / 1000)
      );

      console.log(
        `[AuthReduxSync] Access token fetched, expires in ${expiresIn} seconds`
      );

      // Store token and expiry in Redux
      dispatch(
        setAccessToken({
          token: tokenWithExpiry.accessToken,
          expiresIn,
        })
      );

      return tokenWithExpiry.accessToken;
    } catch (error) {
      console.error("[AuthReduxSync] Failed to fetch access token:", error);
      // Clear token on error
      dispatch(clearAccessToken());
      return null;
    }
  }, [authService, dispatch]);

  // Initial token fetch on login
  useEffect(() => {
    const initializeAuth = async () => {
      if (!isAuthenticated || !authUser?.id) {
        return;
      }

      if (isLoading) {
        console.log(
          "[AuthReduxSync] Skipping auth initialization: Auth service still loading"
        );
        return;
      }

      // Fetch and store access token if we don't have one
      if (!accessToken) {
        console.log(
          "[AuthReduxSync] No access token in Redux, fetching from AuthService..."
        );
        const token = await fetchAndStoreToken();
        if (!token) {
          console.error(
            "[AuthReduxSync] Failed to fetch access token"
          );
        }
      }
    };

    initializeAuth();
  }, [
    isAuthenticated,
    authUser?.id,
    isLoading,
    accessToken,
    fetchAndStoreToken,
  ]);

  // Periodically refresh token in Redux (AuthService handles actual refresh)
  useEffect(() => {
    if (!isAuthenticated) return;

    // Refresh token in Redux every 10 minutes
    // AuthService handles the actual token refresh before expiry
    const intervalId = setInterval(
      async () => {
        console.log(
          "[AuthReduxSync] Periodic token refresh (updating Redux)"
        );
        await fetchAndStoreToken();
      },
      10 * 60 * 1000
    ); // 10 minutes

    return () => {
      clearInterval(intervalId);
    };
  }, [isAuthenticated, fetchAndStoreToken]);

  // Clear Redux state when user logs out
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      console.log(
        "[AuthReduxSync] User logged out, clearing auth state"
      );
      dispatch(clearAuthState());
    }
  }, [isAuthenticated, isLoading, dispatch]);

  return null; // This component doesn't render anything
};
